import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../config/api';

const ArticlesContainer = styled.div`
  padding: 6rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ArticleCard = styled(motion.article)`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px ${props => props.theme.border};
  
  h2 {
    margin: 0 0 1rem;
    font-size: 1.5rem;
    color: ${props => props.theme.text};
  }

  p {
    color: ${props => props.theme.textSecondary};
    margin-bottom: 1rem;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Tag = styled.span`
  background: ${props => props.theme.primary}22;
  color: ${props => props.theme.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.875rem;
`;

const LoadingMessage = styled.p`
  text-align: center;
  margin-top: 2rem;
  color: ${props => props.theme.textSecondary};
`;

const ErrorMessage = styled.p`
  text-align: center;
  margin-top: 2rem;
  color: ${props => props.theme.error};
`;

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.ARTICLES);
        if (!response.ok) {
          throw new Error('Failed to fetch articles');
        }
        const data = await response.json();
        setArticles(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  if (loading) {
    return (
      <ArticlesContainer>
        <LoadingMessage>Loading articles...</LoadingMessage>
      </ArticlesContainer>
    );
  }

  if (error) {
    return (
      <ArticlesContainer>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </ArticlesContainer>
    );
  }

  return (
    <ArticlesContainer>
      <h1>Articles</h1>
      <ArticleGrid>
        {articles.map((article) => (
          <ArticleCard
            key={article.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2>{article.title}</h2>
            <p>{article.content.substring(0, 150)}...</p>
            <p>Published: {new Date(article.publishedAt).toLocaleDateString()}</p>
            <TagContainer>
              {article.tags.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagContainer>
          </ArticleCard>
        ))}
      </ArticleGrid>
    </ArticlesContainer>
  );
};

export default Articles;
